import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import inboLogo from '../img/inbo-logo.png';
import { buildPrintHeader } from './pdf/printHeader';
import { PRINT_WIDTH, PRINT_CSS, buildDensityCss } from './pdf/printStyles';

// ─── 下載豆單 PDF ──────────────────────────────────────────────────────────────
// 設計：不直接截 #root（會帶入 filter、且寬版面導致 A4 上字太小、左右留白）。
// 改成「離屏專用列印容器」.pdf-print-root：
//   1) 只 clone 商品列表（不含 filter）→ 隱藏最上方操作列；
//   2) 固定列印寬度 PRINT_WIDTH（窄寬→A4 字才夠大、可讀性最佳化）；
//   3) 注入 PRINT_CSS + 該版密度 CSS（滿版 + 可讀性補升）；頂部加 INBO logo + 密度標示 + 列印時間；
//   4) html2canvas 截一張高解析 canvas，再切成 A4 直式多頁（滿版、不切斷商品卡）。
//
// 業主需求：一次匯出「一頁 3 / 4 / 5 支」三種密度的 A4 排版，供業主挑選。
// → 同一份商品資料，套三組密度 CSS 各產一個 PDF 檔（網頁顯示不動）。

// 三種列印密度（每頁約 N 支）。thumb 越小、padding/卡距越緊 → 每頁塞越多。
// 業主定案：豆單一律「一頁三支」。
// 為了讓 A4 一頁穩定容下 3 支不被切斷，風味描述在 PDF 夾行(descClamp)、縮圖與間距收斂。
const DENSITIES = [
  { n: 3, label: '', file: '豆單', css: { thumb: 120, headPadV: 8, cellPadV: 6, cardGap: 10, descClamp: 3 } },
];

const PROXY_HOST_SUFFIX = 'shoplineapp.com';

const isProxiable = (src) => {
  try {
    return new URL(src, window.location.href).hostname.endsWith(PROXY_HOST_SUFFIX);
  } catch {
    return false;
  }
};

const toFetchUrl = (src) => {
  const abs = new URL(src, window.location.href).href;
  return `/api/img?url=${encodeURIComponent(abs)}`;
};

// blob → 置中裁切成正方形 → JPEG data URI（商品縮圖用）
const toSquareDataUri = (blob, size) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - side) / 2;
        const sy = (img.naturalHeight - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = reject;
    img.src = objectUrl;
  });

// 任意 URL（同源 / 可代理）→ data URI（logo 用，保留原比例）
const urlToDataUri = (url) =>
  new Promise((resolve, reject) => {
    fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      })
      .catch(reject);
  });

// 把 root 內所有商品圖換成正方形 data URI；回傳「還原函式」
async function inlineProductImages(root) {
  const imgs = [...root.querySelectorAll('img')].filter((el) => isProxiable(el.src));
  const restore = [];
  await Promise.all(
    imgs.map(async (el) => {
      try {
        const resp = await fetch(toFetchUrl(el.src));
        if (!resp.ok) return;
        const blob = await resp.blob();
        const dataUri = await toSquareDataUri(blob, 408); // 136px thumb @ scale 3
        restore.push([el, el.getAttribute('src')]);
        el.setAttribute('src', dataUri);
        await el.decode().catch(() => {});
      } catch {
        /* 單張圖抓不到就維持原樣，不阻斷整份 PDF */
      }
    })
  );
  return () => restore.forEach(([el, src]) => el.setAttribute('src', src));
}

// 現在時間 → "YYYY-MM-DD HH:mm"
function formatNow() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// 依「每頁固定 N 支商品」切頁（業主定案一頁三支）。
// 回傳 [[y0,y1],...]（canvas px）。商品卡 = .js-product-list 內非 full-width 的子元素；
// 產區/處理法標題(full-width)不計入支數，且永遠跟著其後第一支商品同頁（不會落單在頁尾）。
// 每頁恰好 N 支 → 既保證「一頁三支」，也保證商品永不被切斷。
function planPagesByCount(container, scale, perPage, canvasHeight) {
  const grid = container.querySelector('.js-product-list');
  if (!grid) return [[0, canvasHeight]];
  const rootTop = container.getBoundingClientRect().top;
  const children = [...grid.children];
  const isHeader = (ch) =>
    typeof ch.className === 'string' && ch.className.includes('full-width');
  const topOf = (ch) => Math.round((ch.getBoundingClientRect().top - rootTop) * scale);

  const starts = [0]; // 第一頁從容器頂端(含列印 header)開始
  let count = 0;
  for (let i = 0; i < children.length; i++) {
    if (isHeader(children[i])) continue; // 標題不計支數
    if (count === perPage) {
      // 這支是新一頁的第 1 支：把它前面緊鄰的標題一起帶到新頁（標題不孤兒）
      let startIdx = i;
      while (startIdx - 1 >= 0 && isHeader(children[startIdx - 1])) startIdx--;
      starts.push(topOf(children[startIdx]));
      count = 0;
    }
    count++;
  }

  const slices = [];
  for (let k = 0; k < starts.length; k++) {
    const y0 = starts[k];
    const y1 = k + 1 < starts.length ? starts[k + 1] : canvasHeight;
    if (y1 - y0 > 1) slices.push([y0, y1]);
  }
  return slices;
}

// 把父文件所有 same-origin 樣式規則(含 emotion 動態樣式、production speedy 模式)序列化成字串。
// 用於灌進 iframe，讓 iframe 內的版型與畫面一致。
function collectAllCss() {
  let css = '';
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) css += rule.cssText + '\n';
    } catch (e) {
      /* 跨網域 stylesheet 讀不到 cssRules，略過 */
    }
  }
  return css;
}

// 依單一密度設定，在「固定寬度 iframe」內建容器 → 截圖 → 切 A4 多頁 → 存成一個 PDF 檔。
//
// 為何用 iframe：@media(max-width:768px) 看的是「文件 viewport 寬度」。直接在主文件離屏渲染時，
// 手機裝置(viewport≤768)會讓行動版規則套到列印容器，導致「量測(getBoundingClientRect)」與
// 「html2canvas 截圖」版型不一致 → 跨頁把商品攔腰切斷。
// 改在寬度固定 PRINT_WIDTH(820px) 的 iframe 內渲染：iframe 自身 viewport=820，
// @media 一律評估為桌機，且量測與截圖在同一份 820 文件 → 版型必然一致，與裝置無關。
//
// liveList 已先 inline 過商品圖（data-URI），cloneNode 會帶到，故此處只 clone 不再抓圖。
async function renderDensityPdf(liveList, logoSrc, density) {
  const scale = 3; // 提高解析度（A4 縮放後字仍清晰）

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'absolute',
    left: '-99999px',
    top: '0',
    width: `${PRINT_WIDTH}px`,
    height: '100px',
    border: '0',
    background: '#ffffff',
  });
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument || iframe.contentWindow.document;

  try {
    // 1) 複製父文件全部 CSS（含 emotion）到 iframe，版型與畫面一致
    const baseStyle = idoc.createElement('style');
    baseStyle.textContent = collectAllCss();
    idoc.head.appendChild(baseStyle);
    // 2) 列印專用 CSS + 密度覆寫（後出現→勝出）
    const printStyle = idoc.createElement('style');
    printStyle.textContent = PRINT_CSS + buildDensityCss(density.css);
    idoc.head.appendChild(printStyle);
    idoc.body.style.margin = '0';

    const container = idoc.createElement('div');
    container.className = 'pdf-print-root';
    Object.assign(container.style, {
      width: `${PRINT_WIDTH}px`,
      // 小而一致的安全邊距（≈8mm）：避免內容貼死頁緣，同時消除 .App 置中的大片左右留白。
      boxSizing: 'border-box',
      padding: '14px 32px 4px',
      background: '#ffffff',
    });
    container.appendChild(idoc.importNode(buildPrintHeader(formatNow(), logoSrc, density.label), true));

    // clone 商品列表後，移除「下單」欄（PDF 不需互動按鈕）。
    // 整欄移除（含 <col>、<th>、每列 <td>）讓表格自動回填寬度，不留空白欄。
    const listClone = liveList.cloneNode(true);
    listClone
      .querySelectorAll('col.cg-buy, th.col-buy, td.col-buy, .lbl-buy, .buy-cell, .row-buy')
      .forEach((el) => el.remove());
    container.appendChild(idoc.importNode(listClone, true));
    idoc.body.appendChild(container);

    // iframe 高度撐到內容高，確保完整 layout 被渲染
    iframe.style.height = `${container.scrollHeight + 40}px`;

    // 等容器內所有圖解碼完成再截圖
    await Promise.all(
      [...idoc.querySelectorAll('img')].map((img) =>
        img.decode ? img.decode().catch(() => {}) : Promise.resolve()
      )
    );

    const canvas = await html2canvas(container, {
      scale,
      backgroundColor: '#ffffff',
    });
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const A4_W_MM = 210;
    const A4_H_MM = 297;

    // 每頁固定 perPage 支商品切頁（一頁三支，永不切斷商品）
    const slices = planPagesByCount(container, scale, density.n, canvasHeight);

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    slices.forEach(([y0, y1], i) => {
      const sliceH = y1 - y0;
      const tmp = document.createElement('canvas');
      tmp.width = canvasWidth;
      tmp.height = sliceH;
      const ctx = tmp.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, sliceH);
      ctx.drawImage(canvas, 0, y0, canvasWidth, sliceH, 0, 0, canvasWidth, sliceH);

      const imgData = tmp.toDataURL('image/jpeg', 0.85);
      // 預設等比滿版（以頁寬為準）；萬一某頁內容比 A4 高(極端長文)，改以頁高為準縮放並置中，
      // 確保整頁內容都在 A4 內、絕不溢出被裁。
      let wMm = A4_W_MM;
      let hMm = A4_W_MM * (sliceH / canvasWidth);
      let xMm = 0;
      if (hMm > A4_H_MM) {
        const fit = A4_H_MM / hMm;
        hMm = A4_H_MM;
        wMm = A4_W_MM * fit;
        xMm = (A4_W_MM - wMm) / 2;
      }
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', xMm, 0, wMm, hMm);
    });

    pdf.save(`${density.file}.pdf`);
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}

export default async function downloadComponentInPDF(Component) {
  // 要 clone 的商品列表（排除 filter）；找不到才退回 Component
  const liveList = document.querySelector('.js-product-list') || Component;

  // 商品圖只 inline 一次到「畫面上的真 DOM」；三個密度各自 cloneNode 都會帶到 data-URI。
  // 三份 clone 全部建立後才還原真 DOM，避免提前還原導致後續 clone 失去 data-URI。
  const restoreImages = await inlineProductImages(liveList);

  // logo → data URI（同源 bundle，保險用 data-URI 確保 html2canvas 畫得出）
  let logoSrc = inboLogo;
  try {
    logoSrc = await urlToDataUri(inboLogo);
  } catch {
    /* 退回 bundle URL（同源仍可截） */
  }

  try {
    // 依序產三個密度版本的 PDF（一頁 3 / 4 / 5 支）
    for (const density of DENSITIES) {
      await renderDensityPdf(liveList, logoSrc, density);
      // 多檔下載間留緩衝，降低瀏覽器擋連續下載的機率
      await new Promise((r) => setTimeout(r, 500));
    }
  } finally {
    restoreImages();
  }
}
